// src/features/explore/ExplorePage.tsx — public Explore page over the A4 shell.
//
// Two modes, switched on the search box (1.24.00 B3):
//   • box empty / under 2 chars → today's browsing: chips, discovery row,
//     feed tabs + sort over the client-side filterExplore.
//   • box >= 2 chars → the SERVER-driven results view (SearchResults over
//     useSearch: Posts / People / Hashtags with counts, honest empty + error
//     states). filterExplore is no longer consulted in this mode.
// The topbar keeps its `/explore?q=` contract; the box is seeded from ?q= and a
// trending click sets it to "#tag".
import { useMemo, useState } from 'react';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { SearchTools } from '@/features/networks/SearchTools';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { useExplore, useSession, usePageTitle, useSearch } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { GuestBanner } from '@/components/GuestBanner';
import { TrendingPanel } from '@/components/panels/TrendingPanel';
import { WhoToFollowPanel } from '@/components/panels/WhoToFollowPanel';
import { DiscoveryPromo } from '@/components/panels/DiscoveryPromo';
import { ExploreSearch } from './ExploreSearch';
import { ExploreChips } from './ExploreChips';
import { DiscoveryRow } from './DiscoveryRow';
import { ExploreFeedTabs } from './ExploreFeedTabs';
import { ExploreSortMenu } from './ExploreSortMenu';
import { ExploreCard } from './ExploreCard';
import { PeopleResults } from './PeopleResults';
import { CommunityResults } from './CommunityResults';
import { SearchResults } from './SearchResults';
import { FeaturedCollectionsPanel } from './panels/FeaturedCollectionsPanel';
import { ExploreMediaPanel } from './panels/ExploreMediaPanel';
import { filterExplore } from './filterExplore';
import type { ExploreView, FeedTab, ExploreSortId } from './meta';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
`;
const Title = styled.h1`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const BannerSlot = styled.div`
  margin-bottom: 14px;
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 14px 0;
  flex-wrap: wrap;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
  ${media.mobile`grid-template-columns: 1fr;`}
`;
const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

export function ExplorePage() {
  const { isAuthenticated } = useSession();
  usePageTitle('Explore / TruXSocial');
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const { topics, feed } = useExplore('all', 'recent');

  const [activeChip, setActiveChip] = useState<string>('all');
  const [searchText, setSearchText] = useState(q);
  const [seededQ, setSeededQ] = useState(q);
  const [activeTab, setActiveTab] = useState<FeedTab>('for-you');
  const [activeSort, setActiveSort] = useState<ExploreSortId>('recent');

  // Re-seed the search box when the URL ?q= changes (e.g. a new topbar search while
  // already on /explore). Derive-during-render per the React docs' "adjusting state when
  // a prop changes" pattern — avoids the set-state-in-effect anti-pattern.
  if (q !== seededQ) {
    setSeededQ(q);
    setSearchText(q);
  }

  const view: ExploreView =
    activeChip === 'people' ? 'people' : activeChip === 'communities' ? 'communities' : 'posts';
  const topicFilter =
    activeChip === 'all' || activeChip === 'posts' || view !== 'posts' ? 'all' : activeChip;

  // Server search — active from 2 characters (debounced + sequence-guarded in the hook).
  const filterParams = new URLSearchParams();
  for (const key of ['author', 'community', 'topic', 'from', 'until', 'media']) {
    const value = searchParams.get(key);
    if (value && value !== 'any') filterParams.set(key, value);
  }
  const search = useSearch(searchText, 'all', 20, filterParams.toString());

  // Browsing mode only: the box is under the search threshold, so `search: ''`
  // here is deliberate — a 1-character box no longer filters the browse grid
  // (the server view takes over at 2, and one character is not a query).
  const visiblePosts = useMemo(
    () => filterExplore(feed, { topic: topicFilter, search: '', tab: activeTab, sort: activeSort }),
    [feed, topicFilter, activeTab, activeSort],
  );

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="explore">
        <RailPanel id="promo" label="Discover communities">
          <DiscoveryPromo />
        </RailPanel>
        <RailPanel id="people" label="Who to follow">
          <WhoToFollowPanel />
        </RailPanel>
        <RailPanel id="trending" label="Trending">
          <TrendingPanel />
        </RailPanel>
        <RailPanel id="collections" label="Featured collections">
          <FeaturedCollectionsPanel />
        </RailPanel>
        <RailPanel id="media" label="Explore media">
          <ExploreMediaPanel feed={feed} />
        </RailPanel>
      </RailGroup>
    ),
    [feed],
  );
  useRightRail(rightRail);

  return (
    <section>
      <Header>
        <div>
          <Title>Explore</Title>
          <Subtitle>Discover posts, people, topics, communities, and media.</Subtitle>
        </div>
        <NetworkLinks destination="explore" />
      </Header>

      {!isAuthenticated && (
        <BannerSlot>
          <GuestBanner />
        </BannerSlot>
      )}

      <SearchTools
        query={searchText}
        searchField={<ExploreSearch value={searchText} onChange={setSearchText} />}
        activeCategory={!search.active && activeChip !== 'all'}
        onClear={() => setActiveChip('all')}
      >
        {!search.active && (
          <ExploreChips topics={topics} active={activeChip} onChange={setActiveChip} />
        )}
      </SearchTools>

      {search.active ? (
        <SearchResults
          query={searchText}
          loading={search.loading}
          error={search.error}
          results={search.results}
          onPickHashtag={(tag) => setSearchText(`#${tag}`)}
        />
      ) : (
        <>
          {/* A trending topic IS a hashtag search — "#tag" lands in the box and
              the server view takes over. */}
          <DiscoveryRow onSelectTrend={(tag) => setSearchText(`#${tag}`)} />
        </>
      )}

      {!search.active && view === 'posts' && (
        <>
          <Toolbar>
            <ExploreFeedTabs active={activeTab} onChange={setActiveTab} />
            <ExploreSortMenu active={activeSort} onChange={setActiveSort} />
          </Toolbar>
          {visiblePosts.length === 0 ? (
            // Only blame the search when there is one. On an empty box with no chip
            // set — which is what a new member sees on their first visit — "Nothing
            // matches your search" describes a search they never made, and reads as
            // the page being broken rather than the platform being young.
            <Empty>
              {/* `posts` is excluded because it is a VIEW selector, not a filter —
                  line 73 maps it to topicFilter 'all', so tapping it narrows
                  nothing. Treating it as a filter meant a new member who tapped
                  "Posts" with an empty box was still told their search matched
                  nothing, which is the exact defect this branch exists to fix. */}
              {activeChip !== 'all' && activeChip !== 'posts'
                ? 'Nothing matches your search.'
                : 'Nothing to explore yet.'}
            </Empty>
          ) : (
            <Grid>
              {visiblePosts.map((i) => (
                <ExploreCard key={i.id} item={i} />
              ))}
            </Grid>
          )}
        </>
      )}

      {!search.active && view === 'people' && <PeopleResults search="" />}
      {!search.active && view === 'communities' && <CommunityResults search={searchText} />}
    </section>
  );
}
